<template>
  <div class="page-container">
    <h2 class="page-title">商品管理</h2>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input v-model="searchKey" placeholder="搜索商品名称/编码" style="width: 300px" clearable
        @keyup.enter="handleSearch" />
      <el-select v-model="categoryFilter" placeholder="全部分类" clearable style="width: 200px; margin-left: 10px">
        <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id" />
      </el-select>
      <el-button type="primary" @click="handleSearch" style="margin-left: 10px">查询</el-button>
      <el-button @click="resetSearch">重置</el-button>
      <el-button type="success" @click="openAddDialog" style="margin-left: 20px">+ 新增商品</el-button>

      <el-button type="info" @click="handleBatchSync" :loading="batchSyncLoading" style="margin-left: 20px">
        <i class="el-icon-upload2"></i> 批量同步
      </el-button>

      <el-button type="danger" @click="handleBatchUnsync" :loading="batchUnsyncLoading" style="margin-left: 20px">
        <i class="el-icon-delete"></i> 批量取消同步
      </el-button>
    </div>

    <!-- 数据表格 -->
    <el-table :data="goodsList" border v-loading="loading" style="margin-top: 20px">
      <!-- 封面列 -->
      <el-table-column label="封面" width="120" align="center">
        <template #default="{ row }">
          <el-image :src="row.cover_img" fit="cover" style="width:80px;height:80px;border-radius:4px"
            :preview-src-list="[row.cover_img]" :hide-on-click-modal="true">
            <template #error>
              <div class="image-error">
                <i class="el-icon-picture-outline"></i>
              </div>
            </template>
          </el-image>
        </template>
      </el-table-column>

      <!-- 商品信息列 -->
      <el-table-column label="商品信息" width="380">
        <template #default="{ row }">
          <div class="goods-info">
            <div class="goods-name line-clamp">{{ row.name }}</div>
            <div class="goods-code">编码：{{ row.code || '无' }}</div>
            <div class="goods-category">分类：{{ getCategoryName(row.category_id) }}</div>
            <div class="goods-desc line-clamp" v-if="row.good_desc">{{ row.good_desc }}</div>
          </div>
        </template>
      </el-table-column>


      <el-table-column label="同步状态" width="120" align="center">
        <template #default="{ row }">
          <el-tag :type="row.sync_status === 1 ? 'success' : 'warning'">
            {{ row.sync_status === 1 ? '已同步' : '未同步' }}
          </el-tag>
          <div v-if="row.sync_status === 1" class="sync-info">
            <small>{{ row.out_product_id }}</small>
          </div>
        </template>
      </el-table-column>

      <!-- 其他列... -->

      <!-- 价格列 -->
      <el-table-column label="价格" width="220" align="center">
        <template #default="{ row }">
          <span class="price-tag">
            ￥{{ (row.price / 1).toFixed(2) }}
          </span>
        </template>
      </el-table-column>

      <!-- 新增字段列 -->
      <el-table-column label="库存" width="200" align="center">
        <template #default="{ row }">{{ row.quota ?? '不限' }}</template>
      </el-table-column>

      <el-table-column label="销量" width="150" align="center">
        <template #default="{ row }">
          <div>真实：{{ row.take_count }}</div>
          <div>虚拟：{{ row.fictitious_take_count }}</div>
        </template>
      </el-table-column>

      <el-table-column label="推荐" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="row.is_tui ? 'success' : 'info'">
            {{ row.is_tui ? '推荐' : '-' }}
          </el-tag>
        </template>
      </el-table-column>

      <!-- 状态列 -->
      <el-table-column label="状态" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
            {{ row.status === 1 ? '上架中' : '已下架' }}
          </el-tag>
        </template>
      </el-table-column>

      <!-- 操作列 -->
      <el-table-column label="操作" width="480" fixed="right" align="center">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="openEditDialog(row)">编辑</el-button>
          <el-button type="warning" size="small" @click="handleSyncGood(row)" :loading="row.syncLoading"
            :disabled="row.status !== 1">
            {{ row.sync_status === 1 ? '重试' : '同步' }}
          </el-button>
          <!-- 新增：单个取消同步按钮 -->
          <el-button type="danger" size="small" @click="handleUnsyncGood(row)" :loading="row.unsyncLoading"
            :disabled="row.sync_status !== 1">
            取消同步
          </el-button>
          <!-- 新增置顶按钮 -->
          <el-button type="success" size="small" @click="handleSortItem(row)" :loading="row.sortLoading"
            :disabled="row.sync_status !== 1">
            专业号置顶
          </el-button>
          <el-popconfirm title="确定删除该商品？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button type="danger" size="small">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <div class="pagination">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchData" @current-change="fetchData" style="margin-top: 20px" />
    </div>

    <!-- 商品编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑商品' : '新增商品'" width="900px"
      :close-on-click-modal="false">
      <el-form :model="formData" label-width="100px" class="goods-form" ref="formRef">
        <!-- 基础信息 -->
        <div class="form-section">
          <h3 class="form-section-title">基础信息</h3>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="商品名称" prop="name" required>
                <el-input v-model="formData.name" placeholder="请输入商品名称" clearable show-word-limit maxlength="255" />
              </el-form-item>
            </el-col>

            <el-col :span="12">
              <el-form-item label="商品分类" prop="category_id" required>
                <el-select v-model="formData.category_id" placeholder="请选择分类" filterable clearable style="width: 100%">
                  <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="销售价格" prop="price" required>
                <el-input-number v-model="formData.price" :min="0" :precision="2" :step="0.1" controls-position="right"
                  style="width: 100%" placeholder="请输入价格" />
              </el-form-item>
            </el-col>

            <el-col :span="8">
              <el-form-item label="商品编码" prop="code">
                <el-input v-model="formData.code" placeholder="系统自动生成">
                  <template #append>
                    <el-button @click="generateRandomCode">重新生成</el-button>
                  </template>
                </el-input>
              </el-form-item>
            </el-col>

            <el-col :span="8">
              <el-form-item label="排序权重">
                <el-input-number v-model="formData.sort" :min="0" controls-position="right" />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 销售信息 -->
        <div class="form-section">
          <h3 class="form-section-title">销售信息</h3>
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="库存配额">
                <el-input-number v-model="formData.quota" :min="0" placeholder="不限库存" />
              </el-form-item>
            </el-col>

            <el-col :span="8">
              <el-form-item label="虚拟销量">
                <el-input-number v-model="formData.fictitious_take_count" :min="0" />
              </el-form-item>
            </el-col>

            <el-col :span="8">
              <el-form-item label="推荐商品">
                <el-switch v-model="formData.is_tui" :active-value="1" :inactive-value="0" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="配送方式">
                <el-radio-group v-model="formData.purchase_method">
                  <el-radio :label="0">快递发货</el-radio>
                  <el-radio :label="1">到店自提</el-radio>
                  <el-radio :label="2">在线预约</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>

            <el-col :span="12">
              <el-form-item label="商品状态">
                <el-switch v-model="formData.status" :active-value="1" :inactive-value="-1" active-text="上架"
                  inactive-text="下架" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="商品描述">
            <el-input v-model="formData.good_desc" type="textarea" :rows="3" placeholder="请输入简短的商品描述" show-word-limit
              maxlength="1000" />
          </el-form-item>
        </div>

        <!-- 媒体信息 -->
        <div class="form-section">
          <h3 class="form-section-title">媒体信息</h3>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="缩略图">
                <el-upload :file-list="coverFiles" action="#" :limit="1" accept="image/*" :auto-upload="false"
                  :on-change="handleCoverChange" :on-remove="() => formData.cover_img = ''" list-type="picture-card">
                  <el-icon>
                    <Plus />
                  </el-icon>
                  <template #tip>
                    <div class="el-upload__tip">建议尺寸800x800px，仅支持JPEG/PNG</div>
                  </template>
                </el-upload>
              </el-form-item>
            </el-col>

            <el-col :span="12">
              <el-form-item label="首页大图">
                <el-upload :file-list="bigPictureFiles" action="#" :limit="1" accept="image/*" :auto-upload="false"
                  :on-change="handleBigPictureChange" :on-remove="() => formData.big_picture = ''"
                  list-type="picture-card">
                  <el-icon>
                    <Plus />
                  </el-icon>
                </el-upload>
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="轮播内容">
            <el-upload :file-list="rotationFiles" action="#" :multiple="true" accept="image/*,video/*" :limit="5"
              :auto-upload="false" :on-change="handleRotationChange" :on-remove="handleRotationRemove"
              list-type="picture-card" :class="{ 'has-video': hasVideoInRotation }">
              <el-icon>
                <Plus />
              </el-icon>
              <template #file="{ file }">
                <span class="media-remove" @click.stop="handleRotationRemove(file)">×</span>

                <div class="media-preview">
                  <!-- 根据实际数据类型判断 -->
                  <img v-if="file.raw.type.startsWith('image')" :src="file.url" />
                  <video v-else-if="file.raw.type.startsWith('video')" :src="file.url" controls></video>

                </div>
              </template>

              <template #tip>
                <div class="el-upload__tip">轮播图:支持视频轮播和图片轮播</div>
              </template>
            </el-upload>
          </el-form-item>

          <el-form-item label="视频详情">
            <div class="video-uploader">
              <!-- 操作按钮行 -->
              <div class="video-actions">
                <el-upload class="video-upload" action="#" :accept="'video/*'" :auto-upload="false"
                  :show-file-list="false" :on-change="handleVideoChange">
                  <el-button type="primary" size="small" :disabled="!!formData.video__details?.url">
                    <el-icon>
                      <Upload />
                    </el-icon>上传视频
                  </el-button>
                </el-upload>

                <el-button v-if="formData.video__details?.url" type="danger" size="small" @click="removeVideo"
                  style="margin-left: 10px">
                  <el-icon>
                    <Delete />
                  </el-icon>删除视频
                </el-button>

                <el-upload v-if="formData.video__details?.url" class="cover-upload" action="#" :accept="'image/*'"
                  :auto-upload="false" :show-file-list="false" :on-change="handleVideoCoverChange"
                  style="margin-left: 10px">
                  <el-button type="primary" size="small"
                    :disabled="!formData.video__details?.url || !!formData.video__details?.cover">
                    <el-icon>
                      <Picture />
                    </el-icon>上传封面
                  </el-button>
                </el-upload>

                <el-button v-if="formData.video__details?.cover" type="danger" size="small"
                  @click="formData.video__details.cover = null" style="margin-left: 10px">
                  <el-icon>
                    <Delete />
                  </el-icon>删除封面
                </el-button>
              </div>

              <!-- 视频和封面预览 -->
              <div v-if="formData.video__details?.url" class="video-preview-container">
                <div class="video-wrapper">
                  <video controls :src="formData.video__details.url"></video>
                </div>
                <div v-if="formData.video__details?.cover" class="cover-wrapper">
                  <div class="cover-title">视频封面：</div>
                  <img :src="formData.video__details.cover" />
                </div>
              </div>
            </div>
          </el-form-item>
        </div>

        <!-- 商品特征 -->
        <div class="form-section">
          <h3 class="form-section-title">商品特征（最多5项）</h3>
          <div v-for="(feature, index) in formData.good_feature" :key="index" class="feature-item">
            <el-row :gutter="10">
              <el-col :span="4">
                <el-upload action="#" :auto-upload="false" :show-file-list="false"
                  :on-change="(file) => handleFeatureIconChange(file, index)">
                  <div class="feature-icon-upload">
                    <el-icon v-if="!feature.icon">
                      <Plus />
                    </el-icon>
                    <img v-else :src="feature.icon" class="feature-icon" />
                  </div>
                </el-upload>
              </el-col>
              <el-col :span="18">
                <el-input v-model="feature.text" placeholder="输入特征描述" />
              </el-col>
              <el-col :span="2">
                <el-button type="danger" icon="Delete" @click="removeFeature(index)" />
              </el-col>
            </el-row>
          </div>
          <el-button type="primary" size="small" @click="addFeature" :disabled="formData.good_feature.length >= 5">
            + 添加特征
          </el-button>
        </div>

        <!-- 商品详情 -->
        <div class="form-section">
          <h3 class="form-section-title">商品详情</h3>
          <QuillEditor ref="quillRef" v-model:content="formData.goods_details" contentType="html"
            :options="editorOptions" style="height: 400px; border: 1px solid #dcdfe6" />
        </div>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确认提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue' // 添加 computed
import { QuillEditor } from '@vueup/vue-quill'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { ElMessage, ElLoading, ElMessageBox } from 'element-plus'

import Cookies from 'js-cookie'
import config from '@/config'
import axios from 'axios'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
import { Plus, Upload, Delete, Picture } from '@element-plus/icons-vue'
// 上传文件列表
const coverFiles = ref([])
const bigPictureFiles = ref([])
const rotationFiles = ref([])
const quillRef = ref()
const batchSyncLoading = ref(false)
const batchUnsyncLoading = ref(false)

const handleBatchUnsync = async () => {
  try {
    // 获取选中的已同步商品
    const selectedGoods = goodsList.value
      .filter(g => g.sync_status === 1)
      .slice(0, 100);

    if (selectedGoods.length === 0) {
      ElMessage.warning('没有可取消同步的商品');
      return;
    }

    // 修改这里：移除了未使用的 confirm 变量
    await ElMessageBox.confirm(
      `确定要取消同步这 ${selectedGoods.length} 个商品吗？此操作不可逆！`,
      '警告',
      { type: 'warning' }
    );

    const goodIds = selectedGoods.map(g => g.id);
    const res = await axios.post(`${config.BASE_URL}/shop/api/PaymentController.php`, {
      action: 'batch_delete_skus',
      good_ids: goodIds,
      mini_id: selectedGoods[0].mini_id
    });

    if (res.data.success) {
      ElMessage.success(`成功取消同步 ${res.data.affected_rows} 个商品`);
      fetchData();
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(`操作失败: ${error.response?.data?.message || error.message}`);
    }
  }
};

// 单个商品取消同步
const handleUnsyncGood = async (row) => {
  try {
    row.unsyncLoading = true;

    // 确认对话框
    await ElMessageBox.confirm(
      `确定要取消同步商品【${row.name}】吗？此操作不可逆！`,
      '警告',
      { type: 'warning' }
    );

    const res = await axios.post(`${config.BASE_URL}/shop/api/PaymentController.php`, {
      action: 'batch_delete_skus',
      good_ids: [row.id], // 单个商品也用数组形式传递
      mini_id: row.mini_id
    });

    if (res.data.success) {
      ElMessage.success('取消同步成功');
      // 更新本地状态
      Object.assign(row, {
        sync_status: 0,
        out_product_id: null,
        out_sku_id: null,
        last_sync_time: null
      });
    } else {
      throw new Error(res.data.message || '取消同步失败');
    }
  } catch (error) {
    if (error !== 'cancel') { // 忽略用户取消操作
      ElMessage.error(`取消同步失败: ${error.message}`);
    }
  } finally {
    row.unsyncLoading = false;
  }
};

const handleBatchSync = async () => {
  try {
    batchSyncLoading.value = true
    const selectedGoods = goodsList.value.filter(g => g.status === 1) // 只同步上架商品

    for (const good of selectedGoods) {
      await handleSyncGood(good)
      await new Promise(resolve => setTimeout(resolve, 500)) // 添加延迟防止频繁请求
    }

    ElMessage.success(`批量同步完成，共同步 ${selectedGoods.length} 个商品`)
  } catch (error) {
    ElMessage.error(`批量同步出错: ${error.message}`)
  } finally {
    batchSyncLoading.value = false
  }
}
// 新增方法 - 生成6位随机编码
const generateRandomCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  formData.code = result
}

// 处理商品置顶
const handleSortItem = async (row) => {
  try {
    row.sortLoading = true;
    const res = await axios.post(`${config.BASE_URL}/shop/api/PaymentController.php`, {
      action: 'sort_items',
      good_id: row.id,
      mini_id: row.mini_id
    });

    if (res.data.success) {
      ElMessage.success('商品置顶成功');
    } else {
      throw new Error(res.data.message || '置顶失败');
    }
  } catch (error) {
    ElMessage.error(`置顶失败: ${error.message}`);
    console.error('置顶错误详情:', error.response?.data || error);
  } finally {
    row.sortLoading = false;
  }
}

// 处理视频封面上传
const handleVideoCoverChange = async (file) => {
  if (!beforeImageUpload(file.raw)) return false
  const { url } = await uploadFile(file.raw)
  formData.video__details = {
    ...formData.video__details,
    cover: url  // 关键：更新封面地址
  }
}

const customImageHandler = async () => {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', 'image/*')
  input.click()

  input.onchange = async () => {
    const file = input.files[0]
    // 提前声明变量
    let quill = null
    let range = null

    try {
      if (!file || !beforeImageUpload(file)) return

      // 初始化实例
      quill = quillRef.value?.getQuill?.()
      if (!quill) {
        ElMessage.error('编辑器未正确初始化')
        return
      }

      // 获取选区
      range = quill.getSelection()
      if (!range) {
        ElMessage.error('请先点击编辑器选择插入位置')
        return
      }

      quill.insertEmbed(range.index, 'image', '上传中...')
      const { url } = await uploadFile(file)

      quill.deleteText(range.index, 1)
      quill.insertEmbed(range.index, 'image', url)
      quill.setSelection(range.index + 1)
    } catch (error) {
      ElMessage.error('图片上传失败: ' + error.message)
      // 安全清理
      if (quill && range) {
        quill.deleteText(range.index, 1)
      }
    }
  }
}
// Quill配置
const editorOptions = ref({
  modules: {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image', 'video']
      ],
      handlers: {
        image: () => customImageHandler() // 使用箭头函数确保作用域
      }
    }
  },
  placeholder: '请输入商品详情...',
  theme: 'snow'
})


// 处理封面图上传
const handleCoverChange = async (file) => {
  if (!beforeImageUpload(file.raw)) return false
  const { url } = await uploadFile(file.raw)
  formData.cover_img = url
}

// 处理详情大图
const handleBigPictureChange = async (file) => {
  if (!beforeImageUpload(file.raw)) return false
  const { url } = await uploadFile(file.raw)
  formData.big_picture = url
}

// 修复轮播上传问题
const handleRotationChange = async (file) => {  // 参数名称修正
  try {
    if (!beforeMediaUpload(file.raw)) return false
    const { url, type } = await uploadFile(file.raw)

    formData.rotation.push({
      type: type.startsWith('video/') ? 'video' : 'image',
      url
    })

    // 更新预览列表
    rotationFiles.value = [...rotationFiles.value, {
      uid: file.uid,
      name: file.name,
      url: url,
      raw: file.raw
    }]

  } catch (error) {
    ElMessage.error('上传失败: ' + error.message)
    return false
  }
}

const handleSyncGood = async (row) => {
  try {
    row.syncLoading = true

    const res = await axios.post(`${config.BASE_URL}/shop/api/PaymentController.php`, {
      action: 'sync_good',
      good_id: row.id,
      mini_id: row.mini_id
    })

    // 修改这里的判断条件
    if (res.data && (res.data.success || res.data.code === 0)) {
      ElMessage.success('商品同步成功')
      // 更新本地数据
      Object.assign(row, {
        sync_status: 1,
        out_product_id: row.id.toString(), // 确保是字符串
        out_sku_id: `${row.id}_default`,
        last_sync_time: new Date().getTime()
      })
    } else {
      throw new Error(res.data?.msg || '同步失败')
    }
  } catch (error) {
    ElMessage.error(`同步失败: ${error.message}`)
    console.error('同步错误详情:', error.response?.data || error)
  } finally {
    row.syncLoading = false
  }
}

// 处理轮播内容删除
const handleRotationRemove = (file) => {
  const index = formData.rotation.findIndex(item => item.url === file.url)
  if (index !== -1) {
    formData.rotation.splice(index, 1)
  }
  // 从预览列表删除
  const fileIndex = rotationFiles.value.findIndex(f => f.uid === file.uid)
  if (fileIndex !== -1) {
    rotationFiles.value.splice(fileIndex, 1)
  }
}
// 处理视频上传
const handleVideoChange = async (file) => {
  if (!beforeVideoUpload(file.raw)) return false
  const { url } = await uploadFile(file.raw)
  formData.video__details = {
    ...formData.video__details,
    url: url  // 修改关键字段名
  }
}


// 视频检测
const hasVideoInRotation = computed(() => {
  return formData.rotation.some(item => item.type === 'video')
})
// 删除视频
const removeVideo = () => {
  formData.video__details = null
}


// 处理特征图标
const handleFeatureIconChange = async (file, index) => {
  if (!beforeIconUpload(file.raw)) return false
  const { url } = await uploadFile(file.raw)
  formData.good_feature[index].icon = url
}

// 通用上传方法
const uploadFile = async (file) => {
  const signature = await getUploadSignature(file.type.split('/')[0])
  const ossRes = await uploadToOSS(file, signature)
  return {
    url: ossRes.url,
    type: file.type,
    name: file.name
  }
}
// 用户信息
const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id

// 响应式数据
const loading = ref(false)
const submitting = ref(false)
const searchKey = ref('')
const categoryFilter = ref(null)
const dialogVisible = ref(false)
const isEditMode = ref(false)
const goodsList = ref([])
const categories = ref([])
const formRef = ref(null)

// 完整表单数据
const formData = reactive({
  id: null,
  store_id: storeId,
  name: '',
  price: 0,
  code: '',
  category_id: null,
  purchase_method: 1,
  is_tui: 0,
  quota: null,
  take_count: 0,
  fictitious_take_count: 0,
  sort: 0,
  cover_img: '',
  big_picture: '',
  rotation: [],
  good_feature: [],
  video__details: null,
  goods_details: '',
  good_desc: '',
  status: 1
})

// 分页配置
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 修改验证方法（删除类型限制）
const beforeImageUpload = (file) => validateFile(file, { maxSize: 200 })
const beforeVideoUpload = (file) => validateFile(file, { maxSize: 200 })
const beforeIconUpload = (file) => validateFile(file, { maxSize: 200 })

// 修改后的验证方法
const validateFile = (file, { maxSize }) => {
  if (file.size / 1024 / 1024 > maxSize) {
    ElMessage.error(`文件大小不能超过 ${maxSize}MB`)
    return false
  }
  return true
}


// 统一媒体上传处理
const beforeMediaUpload = (file) => {
  if (file.type.startsWith('image/')) return beforeImageUpload(file)
  if (file.type.startsWith('video/')) return beforeVideoUpload(file)
  return true // 允许其他类型
}
// 商品特征操作
const addFeature = () => {
  if (formData.good_feature.length < 5) {
    formData.good_feature.push({ text: '', icon: '' })
  }
}

const removeFeature = (index) => {
  formData.good_feature.splice(index, 1)
}




// 加载数据
// 修改后的数据加载方法
const fetchData = async () => {
  const loadingInstance = ElLoading.service({
    lock: true,
    text: '数据加载中...'
  })
  try {
    const params = {
      store_id: storeId,
      page: pagination.page,
      limit: pagination.limit,
      search: searchKey.value,
      category_id: categoryFilter.value
    }

    const [goodsRes, categoryRes] = await Promise.all([
      axios.get(`${config.BASE_URL}/shop/api/good_api.php`, { params }),
      axios.get(`${config.BASE_URL}/shop/api/grouping_api.php`, {
        params: { store_id: storeId }
      })
    ])

    if (goodsRes.data.code === 200) {
      // 修复点：移除不必要的JSON解析
      goodsList.value = goodsRes.data.data.list.map(item => ({
        ...item,
        // 如果接口返回的是字符串才需要JSON.parse，根据你的接口返回应该是对象
        rotation: item.rotation || [],      // 直接使用数组
        good_feature: item.good_feature || [],
        video__details: item.video__details || null
      }))

      pagination.total = goodsRes.data.data.pagination.total
    }

    if (categoryRes.data.code === 200) {
      categories.value = categoryRes.data.data.list
    }
  } catch (error) {
    ElMessage.error('数据加载失败')
    console.error('加载失败:', error)
  } finally {
    loadingInstance.close()
  }
}
// 获取分类名称
const getCategoryName = (categoryId) => {
  const cat = categories.value.find(c => c.id == categoryId)
  return cat ? cat.name : '未分类'
}

// 搜索处理
const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

// 重置搜索
const resetSearch = () => {
  searchKey.value = ''
  categoryFilter.value = null
  fetchData()
}

// 打开新增弹窗
const openAddDialog = () => {
  isEditMode.value = false
  resetForm()
  dialogVisible.value = true
}

// 修改后的openEditDialog方法
const openEditDialog = (row) => {
  isEditMode.value = true

  // 初始化文件列表
  coverFiles.value = row.cover_img ? [{
    uid: Date.now() + '-cover',
    name: 'cover',
    url: row.cover_img
  }] : []

  bigPictureFiles.value = row.big_picture ? [{
    uid: Date.now() + '-bigpic',
    name: 'big-picture',
    url: row.big_picture
  }] : []

  // 修复轮播初始化
  rotationFiles.value = (row.rotation || []).map((item, index) => ({
    uid: Date.now() + '-rot-' + index,
    name: `media-${index}`,
    url: item.url,
    raw: {
      type: item.type === 'video' ? 'video/mp4' : 'image/jpeg' // 模拟真实文件类型
    }
  }))

  // 正确转换价格（接口返回的是分）
  Object.assign(formData, {
    ...row,
    price: row.price
  })

  // 确保打开弹窗
  dialogVisible.value = true
}

const resetForm = () => {
  // 使用深拷贝初始化
  const initData = {
    id: null,
    store_id: storeId,
    name: '',
    price: 0,
    code: generateRandomCode(), // 自动生成编码
    category_id: null,
    purchase_method: 1,
    is_tui: 0,
    quota: null,
    take_count: 0,
    fictitious_take_count: 0,
    sort: 0,
    cover_img: '',
    big_picture: '',
    rotation: [],
    good_feature: [],
    video__details: null,  // 关键：重置视频数据
    goods_details: '',
    good_desc: '',
    status: 1
  }
  Object.assign(formData, initData)

  // 清空所有上传文件列表
  coverFiles.value = []
  bigPictureFiles.value = []
  rotationFiles.value = []

  // 重置富文本编辑器
  if (quillRef.value) {
    quillRef.value.setHTML('')
  }
}

const handleDelete = async (id) => {
  const loadingInstance = ElLoading.service({
    target: '.page-container',
    text: '删除中...'
  })
  
  try {
    // 获取要删除的商品信息
    const row = goodsList.value.find(item => item.id === id);
    if (!row) {
      throw new Error('商品不存在');
    }

    // 如果是已同步状态，先取消同步
    if (row.sync_status === 1) {
      await ElMessageBox.confirm(
        '该商品已同步到小红书，删除将同时取消同步，确定继续吗？',
        '警告',
        { type: 'warning' }
      );
      
      // 调用取消同步接口
      const unsyncRes = await axios.post(`${config.BASE_URL}/shop/api/PaymentController.php`, {
        action: 'batch_delete_skus',
        good_ids: [row.id],
        mini_id: row.mini_id || 1
      });
      
      if (!unsyncRes.data.success) {
        throw new Error('取消同步失败: ' + (unsyncRes.data.message || '未知错误'));
      }
    }

    // 删除本地记录
    const deleteRes = await axios.delete(`${config.BASE_URL}/shop/api/good_api.php`, {
      data: {
        id,
        store_id: storeId
      }
    });
    
    if (deleteRes.data.code !== 200) {
      throw new Error(deleteRes.data.message || '删除失败');
    }
    
    ElMessage.success('删除成功');
    fetchData();
  } catch (error) {
    if (error !== 'cancel') { // 忽略用户取消操作
      ElMessage.error(error.response?.data?.message || error.message);
    }
  } finally {
    loadingInstance.close();
  }
}

// 提交表单
const submitForm = async () => {
  submitting.value = true
  try {
    // 表单验证
    if (!formData.name.trim()) throw new Error('请输入商品名称')
    if (formData.name.length > 255) throw new Error('商品标题不能超过255个字符')
    if (!formData.category_id) throw new Error('请选择商品分类')
    if (formData.good_feature.some(f => !f.text.trim())) throw new Error('特征描述不能为空')
    if (formData.good_desc && formData.good_desc.length > 1000) {
      throw new Error('商品描述不能超过1000个字符')
    }
    // 自动生成编码（如果为空）
    if (!formData.code) {
      generateRandomCode()
    }


    // 构造符合要求的video__details结构
    const videoDetails = formData.video__details ? {
      url: formData.video__details.url,
      cover: formData.video__details.cover || null
    } : null

    // 构造请求数据
    const payload = {
      ...formData,
      price: formData.price,
      rotation: JSON.stringify(formData.rotation),
      good_feature: JSON.stringify(formData.good_feature),
      video__details: videoDetails ? JSON.stringify(videoDetails) : null, // 使用新结构

      big_picture: formData.big_picture,
      good_desc: formData.good_desc.substring(0, 1000), // 确保不超过1000字符

      sort: formData.sort,
      is_tui: formData.is_tui ? 1 : 0
    }

    // 清理字段
    delete payload.create_time
    delete payload.update_time

    // 请求处理
    const method = isEditMode.value ? 'put' : 'post'
    const res = await axios[method](`${config.BASE_URL}/shop/api/good_api.php`, payload)

    if (res.data.code === 200) {
      ElMessage.success(isEditMode.value ? '更新成功' : '新增成功')
      dialogVisible.value = false
      fetchData()
    } else {
      throw new Error(res.data.message || '操作失败');
    }
  } catch (error) {
    console.error('提交失败:', error);

    // 修改这里：显示完整的错误详情
    let errorMessage = error.message;

    // 如果是axios错误，尝试获取更详细的错误信息
    if (error.response) {
      errorMessage = error.response.data?.message || error.response.statusText;
    } else if (error.request) {
      errorMessage = '请求未收到响应';
    }

    // 显示错误提示（持续时间设为5秒，让用户有足够时间阅读）
    ElMessage.error({
      message: `操作失败: ${errorMessage}`,
      duration: 5000, // 5秒
      showClose: true // 显示关闭按钮
    });
  } finally {
    submitting.value = false;
  }
}

// 初始化加载
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-title {
  font-size: 20px;
  color: #333;
  margin-bottom: 25px;
}

/* 调整删除按钮位置 */
.media-remove {
  position: absolute;
  right: 10px;
  /* 调整定位 */
  top: 10px;
  width: 20px;
  height: 20px;
  background: #f56c6c;
  border-radius: 50%;
  color: white;
  text-align: center;
  line-height: 18px;
  cursor: pointer;
  font-size: 16px;
  transform: translate(50%, -50%);
  /* 添加偏移确保按钮可见 */
  z-index: 2;
  /* 确保按钮在最上层 */
  overflow: visible;
  /* 修改关键属性 */
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.goods-info {
  line-height: 1.6;

  .line-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    /* 限制显示行数 */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    /* 确保长单词或URL也能换行 */
  }

  .goods-name {
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 4px;

  }

  .goods-desc {
    font-size: 12px;
    color: #888;
    margin-top: 5px;
  }

  .goods-code,
  .goods-category {
    font-size: 12px;
    color: #666;
  }

  .goods-desc {
    margin-top: 5px;
    color: #888;
  }
}

.price-tag {
  color: #f56c6c;
  font-weight: 500;
}

.form-section {
  margin-bottom: 30px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;

  .form-section-title {
    font-size: 16px;
    color: #333;
    margin-bottom: 15px;
    padding-left: 10px;
    border-left: 4px solid #409eff;
  }
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.image-error {
  width: 80px;
  height: 80px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
}

.feature-item {
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;

  &:hover {
    border-color: #409eff;
  }
}


/* 新增样式 */
.upload-wrapper {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 10px;
}

.feature-icon-upload {
  width: 60px;
  height: 60px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  .el-icon {
    font-size: 20px;
    color: #8c939d;
  }
}

.feature-icon {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 视频上传区域样式 */
.video-uploader {
  .video-actions {
    display: flex;
    align-items: center;
    margin-bottom: 15px;

    .el-button {
      display: flex;
      align-items: center;

      .el-icon {
        margin-right: 5px;
      }
    }
  }

  .video-preview-container {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 15px;
    background: #f8f9fa;

    .video-wrapper {
      max-width: 500px;
      margin-bottom: 15px;

      video {
        width: 100%;
        border-radius: 4px;
        background: #000;
      }
    }

    .cover-wrapper {
      display: flex;
      align-items: center;
      margin-top: 10px;

      .cover-title {
        color: #606266;
        margin-right: 10px;
        font-size: 14px;
      }

      img {
        max-width: 150px;
        max-height: 100px;
        border-radius: 4px;
        border: 1px solid #dcdfe6;
      }
    }
  }
}

/* 修改媒体预览容器 */
.media-preview {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;

  img,
  video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    /* 保持比例完整显示 */
  }

  video {
    width: 100%;
    height: 100%;
  }
}



/* 视频存在时的提示 */
.has-video .el-upload {
  &:hover {
    border-color: #ffa940;

    .el-icon {
      color: #ffa940;
    }
  }
}

/* 自定义编辑器样式 */
.ql-toolbar {
  border-radius: 4px 4px 0 0;
  border-color: #dcdfe6 !important;
}

.ql-container {
  border-radius: 0 0 4px 4px;
  border-color: #dcdfe6 !important;
}

/* 上传中的占位符样式 */
img[src="上传中..."] {
  background: #f5f7fa;
  padding: 20px;
  color: #909399;
  font-style: italic;
}

/* 添加以下样式 */
:deep(.el-upload-list--picture-card .el-upload-list__item) {
  width: 148px;
  height: 148px;
  overflow: hidden;
  /* 保留hidden防止内容溢出 */
  position: relative;
}



.video-preview {
  margin-top: 10px;

  video {
    max-width: 300px;
    border-radius: 4px;
  }

  .video-cover {
    width: 80px;
    height: 80px;
    object-fit: cover;
    margin-left: 10px;
  }
}

.char-counter {
  text-align: right;
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.sync-info {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
  word-break: break-all;
}

/* 使操作按钮不换行 */
.el-table-column--fixed-right .cell {
  display: flex;
  flex-wrap: nowrap;
}
</style>